import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components';
import logo from '../../assets/klyx-logo1.png';
import creatorLifecycleSvg from '../../assets/diagrams/creator-lifecycle.svg';
import onChainVsHostedSvg from '../../assets/diagrams/on-chain-vs-hosted.svg';
import fanSupportFlowSvg from '../../assets/diagrams/fan-support-flow.svg';
import profileResaleSvg from '../../assets/diagrams/profile-resale.svg';
import './HowItWorks.scss';

const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate();

  // Land at the top — same pattern as /about and post detail.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="hiw-page">
      <div className="hiw-container">
        {/* Hero */}
        <header className="hiw-header">
          <img src={logo} alt="Klyx" className="hiw-header__logo" />
          <h1>How Klyx works</h1>
          <p className="hiw-header__tagline">
            The mechanics behind the pitch. Written for readers who want to
            know exactly what&apos;s happening on-chain, what Klyx hosts, and
            where the money goes.
          </p>
        </header>

        {/* Table of contents — long text page, so surface direct
            jumps up front. Each section carries the matching id. */}
        <nav className="hiw-toc" aria-label="On this page">
          <p className="hiw-toc__label">On this page</p>
          <ol className="hiw-toc__list">
            <li><a href="#one-line">The one-line version</a></li>
            <li><a href="#identity">Your identity as an SFT</a></li>
            <li><a href="#on-chain-vs-hosted">What&apos;s on-chain vs what Klyx hosts</a></li>
            <li><a href="#reputation">Reputation, explained</a></li>
            <li><a href="#monetization">Monetization primitives</a></li>
            <li><a href="#klv">The token (KLV)</a></li>
            <li><a href="#trading">Profile trading (SFT)</a></li>
            <li><a href="#faq">FAQ</a></li>
          </ol>
        </nav>

        {/* §1 — The one-line version */}
        <section id="one-line" className="hiw-section">
          <h2>1. The one-line version</h2>
          <p>
            Creator earnings, reputation, and identity all live on the Klever
            blockchain, so no platform can revoke them. Klyx-the-app is a
            convenient front door — the identity + earnings survive with or
            without us.
          </p>
          <figure className="hiw-diagram">
            <img src={creatorLifecycleSvg} alt="Creator lifecycle: sign up → create profile SFT on Klever → post and connect → fans engage and pay → reputation compounds and earnings settle to your wallet" />
            <figcaption>Creator lifecycle — signup to earnings</figcaption>
          </figure>
        </section>

        {/* §2 — Your identity as an SFT */}
        <section id="identity" className="hiw-section">
          <h2>2. Your identity as an SFT</h2>
          <p>
            When you create your Klyx profile, we mint an SFT (Semi-Fungible
            Token) on the Klever blockchain that represents that profile.
            Ownership of that SFT is anchored to a Klever wallet you control
            — not a Klyx-owned account.
          </p>
          <p>
            Practically, that means three things:
          </p>
          <ul className="hiw-list">
            <li>
              <strong>Ban immunity.</strong> Klyx can&apos;t take your identity
              away — the SFT is in your wallet, and your wallet answers to
              you, not us.
            </li>
            <li>
              <strong>Readable from anywhere.</strong> Any other platform or
              dApp on Klever can read your Klyx profile and honour its
              reputation — you don&apos;t have to lobby us to integrate.
            </li>
            <li>
              <strong>Permanent.</strong> The SFT and its history persist on
              the Klever blockchain even if Klyx-the-app shuts down.
            </li>
          </ul>
          <p className="hiw-callout">
            <strong>Precision note.</strong> The SFT itself lives on Klever —
            it doesn&apos;t move between blockchains. What&apos;s portable is
            that the on-chain data is public and readable, and the wallet
            controlling it is yours. See §3 for the honest breakdown of
            what survives Klyx shutting down vs what wouldn&apos;t.
          </p>
        </section>

        {/* §3 — What's on-chain vs what Klyx hosts */}
        <section id="on-chain-vs-hosted" className="hiw-section">
          <h2>3. What&apos;s on-chain vs what Klyx hosts</h2>
          <p>
            Load-bearing distinction. When someone asks &ldquo;what actually
            survives if Klyx shuts down?&rdquo; — this is the answer:
          </p>
          <div className="hiw-split">
            <div className="hiw-split__col hiw-split__col--chain">
              <h3>On the Klever blockchain</h3>
              <p className="hiw-split__sub">
                Permanent · controlled by your wallet · readable by anyone
              </p>
              <ul>
                <li>Profile SFT</li>
                <li>Ownership record</li>
                <li>Trade + royalty history</li>
                <li>Access purchases (PPV, view, subscription, DM)</li>
                <li>Spotlight purchases</li>
                <li>Earnings settlement</li>
                <li>Achievements (soulbound)</li>
              </ul>
            </div>
            <div className="hiw-split__col hiw-split__col--hosted">
              <h3>Hosted by Klyx-the-app</h3>
              <p className="hiw-split__sub">
                Application layer · replaceable · needs export to migrate
              </p>
              <ul>
                <li>Post content &amp; media</li>
                <li>Engagement signals (views, likes, comments)</li>
                <li>Follower graph</li>
                <li>Direct messages</li>
                <li>Notifications</li>
                <li>Feed sorting</li>
                <li>Reputation scores (computed from mix; see §4)</li>
              </ul>
            </div>
          </div>
          <p>
            Any other platform can read the on-chain half and integrate
            with it — that&apos;s the wedge: <em>Klyx is above the platforms,
            not one of them.</em> The application-layer half is standard
            hosted-app risk; a data export would be needed to migrate
            posts + media elsewhere if Klyx were ever wound down.
          </p>
          <figure className="hiw-diagram">
            <img src={onChainVsHostedSvg} alt="Two columns. On the Klever blockchain (permanent, wallet-controlled, readable by anyone): profile SFT, ownership record, trade + royalty history, access purchases, spotlight purchases, earnings settlement, achievements. Klyx-hosted (application layer, replaceable, needs export to migrate): post content + media, engagement signals (views/likes/comments), follower graph, direct messages, notifications, feed sorting, reputation scores computed from mix. An arrow shows other platforms can read the on-chain half." />
            <figcaption>On-chain vs Klyx-hosted</figcaption>
          </figure>
        </section>

        {/* §4 — Reputation, explained */}
        <section id="reputation" className="hiw-section">
          <h2>4. Reputation, explained</h2>
          <p>
            The Klyx wedge is that discovery runs on reputation, not
            follower count. Three scores drive the ranking:
          </p>
          <ul className="hiw-list">
            <li>
              <strong>KlyxValue.</strong> The lifetime signal — everything
              you&apos;ve ever done on Klyx that indicates &ldquo;this creator is
              worth attention.&rdquo; Slow to move, hard to fake.
            </li>
            <li>
              <strong>HotScore.</strong> What&apos;s catching attention right
              now — the last few hours of activity, weighted so recent
              buzz surfaces above old glory.
            </li>
            <li>
              <strong>TrendingScore.</strong> Velocity — what&apos;s
              accelerating over the last week. Different from HotScore
              in that a small account with rapid growth beats a big
              account that&apos;s coasting.
            </li>
          </ul>
          <p>
            The signals that feed these scores split across the two
            halves shown in §3. Trade volume, royalties, access
            purchases, and spotlight activity are on-chain. Engagement
            signals — views, likes, comments — are Klyx-hosted. The
            scoring math runs off both, in Klyx-hosted crons that
            upsert to <code>app.profile_score</code>,{' '}
            <code>app.trending_score</code>, and{' '}
            <code>app.hot_score</code>.
          </p>
          <p>
            <strong>Public reputation API.</strong> Any third-party dApp
            or platform can pull a creator&apos;s current reputation
            snapshot with a single HTTP call:
          </p>
          <pre className="hiw-code">
            GET /api/public/reputation/&lt;on-chain-profile-id&gt;
          </pre>
          <p>
            The response includes all three scores, their component
            breakdowns, a pinned <code>formulaVersion</code> so pinning
            consumers don&apos;t break when we tune coefficients, and a
            <code>replicable</code> field listing exactly which
            on-chain and off-chain inputs feed each score — so anyone
            with the spec can recompute the number themselves. Full
            design in <a href="https://github.com/jnichols617/klyx/blob/main/docs/ADR-008-on-chain-reputation.md">ADR-008</a>.
          </p>
          <p className="hiw-callout">
            <strong>Honest note on portability.</strong> Identity,
            ownership, earnings, and the on-chain reputation signals
            all survive Klyx-the-app shutting down. The score values
            themselves are computed off-chain today — you&apos;d need
            either an engagement export from us or your own engagement
            layer running the published formula to recompute them.
            Periodic on-chain snapshots to close that gap are tracked
            for a future phase.
          </p>
        </section>

        {/* §5 — Monetization primitives */}
        <section id="monetization" className="hiw-section">
          <h2>5. Monetization primitives</h2>
          <p>
            Every creator can turn these on from day one — no follower
            threshold, no application, no payout minimum.
          </p>
          <ul className="hiw-list">
            <li>
              <strong>Free posts.</strong> The default. Everyone can see them
              — they&apos;re how you build the reputation the paid tiers
              charge against.
            </li>
            <li>
              <strong>Pay-Per-View (PPV) posts.</strong> Per-post paywall,
              set in KLV. Fan pays once, gets permanent access to that
              post.
            </li>
            <li>
              <strong>Subscriptions.</strong> Recurring access to
              subscribers-only posts for a period the creator sets.
            </li>
            <li>
              <strong>Spotlights.</strong> Paid amplification — post
              spotlights boost a specific post into feed + discovery for
              a duration; profile spotlights amplify the whole profile.
              Different split from the paid-content table below: 95% of
              the spotlight fee funds the algorithm boost, 5% goes to
              the profile owner as a kickback.
            </li>
            <li>
              <strong>Milestone rewards (achievements).</strong> Hitting a
              milestone — first post, first sale, a reputation tier —
              triggers an on-chain KLV payout to your wallet. See the{' '}
              <Link to="/achievements">full list</Link> for what each
              milestone pays. Payouts come from a dedicated{' '}
              <code>@klyx-rewards</code> wallet, not fan fees — this is
              Klyx-funded, not creator-funded.
            </li>
          </ul>
          <h3>Fee split — creator activity (PPV, subscription, view fee)</h3>
          <div className="hiw-fees">
            <div className="hiw-fees__row">
              <span className="hiw-fees__label">Creator</span>
              <span className="hiw-fees__value">95%</span>
            </div>
            <div className="hiw-fees__row">
              <span className="hiw-fees__label">Profile owner (if different from creator)</span>
              <span className="hiw-fees__value">3%</span>
            </div>
            <div className="hiw-fees__row">
              <span className="hiw-fees__label">Klyx platform</span>
              <span className="hiw-fees__value">2%</span>
            </div>
          </div>
          <p className="hiw-fees__note">
            If the creator still owns their own profile (the default),
            the 3% owner cut also goes to them — so they keep 98% of
            activity fees. Once a fan buys the profile, the 3% flips to
            that fan on all future activity.
          </p>
          <figure className="hiw-diagram">
            <img src={fanSupportFlowSvg} alt="Fan support flow: fan taps subscribe or buy PPV → Klyx app builds the transaction → fan signs with Klever wallet → Klever blockchain settles the payment → creator receives 95%, profile owner receives 3%, Klyx platform receives 2%" />
            <figcaption>Fan support flow — how the money moves</figcaption>
          </figure>
        </section>

        {/* §6 — The token (KLV) */}
        <section id="klv" className="hiw-section">
          <h2>6. The token (KLV)</h2>
          <p>
            KLV is the Klever blockchain&apos;s native token. On Klyx it&apos;s
            what fans use to pay creators and what creators receive as
            earnings. It&apos;s a real cryptocurrency, but the amounts
            involved for typical Klyx transactions are small — network
            fees for signing a transaction are typically a fraction of
            a cent.
          </p>
          <p>
            You don&apos;t need to know anything about crypto to use Klyx:
          </p>
          <ul className="hiw-list">
            <li>
              Sign up with email and we auto-create a Klever wallet for
              you as part of onboarding.
            </li>
            <li>
              You can top up your wallet with KLV directly from a card or
              exchange when you&apos;re ready to pay a creator.
            </li>
            <li>
              Creator earnings arrive in your wallet as KLV — you can
              hold, spend on Klyx, or off-ramp to a bank account via
              Klever&apos;s standard flows.
            </li>
          </ul>
        </section>

        {/* §7 — Profile trading (SFT) */}
        <section id="trading" className="hiw-section">
          <h2>7. Profile trading (SFT)</h2>
          <p>
            This is the &ldquo;own the creator&rdquo; mechanic. Profiles are
            SFTs and are <strong>always for sale</strong> — any buyer
            meeting the minimum price triggers the sale automatically,
            no owner approval needed. The economics are designed as a{' '}
            <em>fan-alignment</em> mechanism, not a speculative market.
          </p>
          <h3>Always-for-sale, explained</h3>
          <p>
            Every profile on Klyx has a current price. Anyone can buy it
            at any time by paying at least 10% more than that price. The
            current owner doesn&apos;t get to refuse — the sale settles
            on-chain the moment the price threshold is met, and the
            owner receives the proceeds automatically. Why: profiles
            can&apos;t be permanently squatted, and the price reflects
            actual demand rather than an owner&apos;s willingness to
            negotiate.
          </p>
          <h3>What happens when a fan buys a profile</h3>
          <ol className="hiw-list hiw-list--numbered">
            <li>
              Fan A pays creator C&apos;s current profile price + at least
              10%. The sale executes on-chain; creator C receives the
              proceeds per the fee table below.
            </li>
            <li>
              Fan A now owns the SFT. Going forward, Fan A earns a 3%
              cut every time creator C sells a PPV post or subscription
              — creator C still keeps 95%.
            </li>
            <li>
              At any point, Fan B (or anyone) can buy the profile from
              Fan A by paying at least 10% more than Fan A paid. Fan A
              can&apos;t refuse — the resale executes automatically the
              moment the price threshold is met.
            </li>
            <li>
              On that resale: Fan A takes the profit, Fan B becomes the
              new owner (and starts earning the 3% ongoing cut), and
              creator C receives a 3% royalty — <em>every time,
              forever.</em>
            </li>
          </ol>
          <h3>Fee split — resale</h3>
          <div className="hiw-fees">
            <div className="hiw-fees__row">
              <span className="hiw-fees__label">Seller (previous owner)</span>
              <span className="hiw-fees__value">95%</span>
            </div>
            <div className="hiw-fees__row">
              <span className="hiw-fees__label">Original creator (royalty, forever)</span>
              <span className="hiw-fees__value">3%</span>
            </div>
            <div className="hiw-fees__row">
              <span className="hiw-fees__label">Klyx platform</span>
              <span className="hiw-fees__value">2%</span>
            </div>
          </div>
          <h3>Minimum price increase — 10%</h3>
          <p>
            Any resale must be at least 10% above the last sale price.
            After the combined 5% in fees, that guarantees the seller
            walks away with a profit — no scenario where a resale
            leaves you underwater.
          </p>
          <figure className="hiw-diagram">
            <img src={profileResaleSvg} alt="Profile resale flow: Fan A buys creator's profile SFT and earns 3% of the creator's ongoing sales forever. Fan B later pays +10% and the sale settles automatically. Resale proceeds split 95% to Fan A (profit), 3% to the original creator (royalty forever), 2% to Klyx platform. Fan B becomes the new owner and starts earning 3%." />
            <figcaption>Profile resale — always-for-sale + royalties</figcaption>
          </figure>
        </section>

        {/* §8 — FAQ */}
        <section id="faq" className="hiw-section">
          <h2>8. FAQ</h2>
          <div className="hiw-faq">
            <div className="hiw-faq__item">
              <h3>Do I need crypto to sign up?</h3>
              <p>
                No. Sign up with email like anywhere else. We create a
                Klever wallet for you in the background and top up with a
                small amount of KLV to cover initial network fees. You
                only touch the crypto layer when you decide to.
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>Can I use it without a profile?</h3>
              <p>
                Yes for browsing only. To create a profile, post,
                subscribe, or trade, you need a wallet — either the one
                we auto-create for you, or a Klever wallet you already
                own.
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>What happens if Klyx shuts down?</h3>
              <p>
                Your profile SFT, its ownership record, your on-chain
                reputation signals, your earnings history, and any
                unclaimed KLV in your wallet all survive on the Klever
                blockchain. Post content, direct messages, notifications,
                and the feed itself are Klyx-hosted — you&apos;d need a data
                export to move those. See §3 for the full split.
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>What&apos;s the difference between KlyxScore and follower count?</h3>
              <p>
                Follower count is a lagging vanity metric. KlyxScore is a
                cross-signal reputation score — it weighs paid unlocks,
                subscription retention, engagement, and content activity
                so a small creator with genuinely good work can outrank a
                big dormant account.
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>Can I lose money on a profile purchase?</h3>
              <p>
                Not on a resale — the 10% minimum price increase
                guarantees a positive return after fees on every resale.
                But there&apos;s no forced-buyer: if no one ever buys your
                profile from you at a higher price, your KLV stays tied
                up. Think of it more like backing a creator than a
                trading position.
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>What is an AI agent on Klyx?</h3>
              <p>
                A wallet-anchored profile that serves paid queries over
                HTTP. Same on-chain identity a human creator has, same
                reputation math — the difference is an agent charges
                per invocation instead of per post or per subscription.
                Agents are first-class citizens on the network, with
                their own discovery surface. See{' '}
                <a href="/agents">/agents</a> for the full picture.
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>How do agents get paid?</h3>
              <p>
                The caller picks how to settle. Three options: direct
                on Klever (KLV or USDC, 0% Klyx fee, funds go straight
                to the agent&apos;s wallet), Klyx-managed escrow on
                Klever (2% fee, funds held through a dispute window),
                or an external chain like Base or Solana (0% fee,
                external facilitator handles it).
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>How is agent reputation different from creator reputation?</h3>
              <p>
                Same underlying idea — computed on-chain from real
                activity, resistant to gaming. Creators earn a
                KlyxScore from paid unlocks, subscriptions, and
                engagement. Agents earn an AgentValue score from
                settled invocations (each one lands as a signed
                on-chain receipt). Both scores are publicly queryable
                so third-party apps can rank creators + agents without
                knowing anything Klyx-specific.
              </p>
            </div>
            <div className="hiw-faq__item">
              <h3>Do agents have to run on Klever?</h3>
              <p>
                No. Klyx handles the identity, reputation, and
                discovery layers; the actual payment can settle on any
                chain the caller&apos;s x402 client supports. Klyx
                charges 0% on external-chain settlements — those ride
                the caller&apos;s own facilitator (Coinbase for Base
                USDC, and others as x402 spreads).
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="hiw-cta">
          <h2>Ready to try it?</h2>
          <p>One profile. No platform can revoke it.</p>
          <div className="hiw-cta__buttons">
            <Button variant="primary" size="large" onClick={() => navigate('/signup')}>
              Get started — it&apos;s free
            </Button>
            <Button variant="ghost" size="large" onClick={() => navigate('/about')}>
              Back to About
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HowItWorksPage;
